import { InspectorContext } from "../reducer";
import { PageControls } from "../../../common/PageControls";
import { String } from "@/util";
import { useConfiguration } from "@/queries/configuration.queries";
import { useContext } from "react";
import { useGetDatasheetsBySubProject } from "@/queries/datasheet.queries";
import { useGetOperations, useQualityPlanLocation } from "../queries";
import { useGetProjectList } from "@/queries/project.queries";
import { useGetSubProjectsByProject } from "@/queries/subProject.queries";

import styles from "./OperationSelection.module.scss";

export function OperationSelection() {
  const { state, updateState } = useContext(InspectorContext);

  const config = useConfiguration();
  const projects = useGetProjectList();
  const subProjects = useGetSubProjectsByProject(state.project?.id);
  const datasheets = useGetDatasheetsBySubProject(state.subProject?.id);
  const qualityPlan = useQualityPlanLocation(state.subProject?.id);
  const operations = useGetOperations(state.datasheet?.datasheetId ?? state.subProject?.id, state.discipline);

  return (
    <div className={styles.container}>
      <div className="row mt-2">
        <div className="col-xs-auto col-md-3">
          <label htmlFor="project">{config.data?.projectName ?? "Project"}</label>
        </div>
        <div className="col-xs-auto col-md-9">
          <select
            name="project"
            className="form-select"
            value={state.project?.id ?? String.Empty}
            disabled={projects.isLoading}
            onChange={(e) =>
              updateState({
                type: "Project",
                payload: projects.data?.find((x) => x.id === e.currentTarget.value),
              })
            }
          >
            <option />
            {projects.isSuccess &&
              projects.data
                .filter((x) => x.stage === "Fabrication")
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
          </select>
        </div>
      </div>
      <div className="row mt-2">
        <div className="col-xs-auto col-md-3">
          <label htmlFor="subproject">{config.data?.subProjectName ?? "Sub-Project"}</label>
        </div>
        <div className="col-xs-auto col-md-9">
          <select
            name="subproject"
            className="form-select"
            value={state.subProject?.id ?? String.Empty}
            disabled={!state.project || subProjects.isLoading}
            onChange={(e) =>
              updateState({
                type: "SubProject",
                payload: subProjects.data?.find((x) => x.id === e.currentTarget.value),
              })
            }
          >
            <option />
            {subProjects.isSuccess &&
              subProjects.data.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
          </select>
        </div>
      </div>
      {qualityPlan.data?.isAtDatasheetLevel && (
        <div className="row mt-2">
          <div className="col-xs-auto col-md-3">
            <label htmlFor="datasheet">{config.data?.datasheetName ?? "Datasheet"}</label>
          </div>
          <div className="col-xs-auto col-md-9">
            <select
              name="datasheet"
              className="form-select"
              value={state.datasheet?.datasheetId ?? String.Empty}
              disabled={!state.subProject || datasheets.isLoading}
              onChange={(e) =>
                updateState({
                  type: "Datasheet",
                  payload: datasheets.data?.find((x) => x.datasheetId === e.currentTarget.value),
                })
              }
            >
              <option />
              {datasheets.isSuccess &&
                datasheets.data.map((x) => (
                  <option key={x.datasheetId} value={x.datasheetId}>
                    {x.datasheetName}
                  </option>
                ))}
            </select>
          </div>
        </div>
      )}
      <div className="row mt-2">
        <div className="col-xs-auto col-md-3">
          <label htmlFor="discipline">Inspection Type</label>
        </div>
        <div className="col-xs-auto col-md-9">
          <select
            name="discipline"
            className="form-select"
            value={state.discipline ?? String.Empty}
            disabled={!state.subProject || (qualityPlan.data?.isAtDatasheetLevel && !state.datasheet)}
            onChange={(e) =>
              updateState({
                type: "Discipline",
                payload: e.currentTarget.value,
              })
            }
          >
            <option>BWI</option>
            <option>VT</option>
            <option>PT</option>
            <option>UT</option>
            <option>ET</option>
            <option>PMIVT</option>
            <option>PMIPT</option>
            <option>PMIUT</option>
            <option>PMIET</option>
          </select>
        </div>
      </div>
      <div className="row mt-2">
        <div className="col-xs-auto col-md-3">
          <label htmlFor="operation">Operation</label>
        </div>
        <div className="col-xs-auto col-md-9">
          <select
            name="operation"
            className="form-select"
            value={state.operation ?? String.Empty}
            disabled={
              !state.subProject || operations.isLoading || (qualityPlan.data?.isAtDatasheetLevel && !state.datasheet)
            }
            onChange={(e) =>
              updateState({
                type: "Operation",
                payload: e.currentTarget.value,
              })
            }
          >
            <option />
            {operations.isSuccess &&
              operations.data.map((operation) => (
                <option key={operation.number} value={operation.number}>
                  {operation.withDescription}
                </option>
              ))}
          </select>
        </div>
      </div>

      <PageControls showNext allowNext={Boolean(state.operation)} />
    </div>
  );
}
